import React, { useState, useEffect, useContext, useCallback, useRef } from 'react'
import {
      CCard,
      CCardHeader,
      CCardBody,
      CButton,
      CModal,
      CModalHeader,
      CModalTitle,
      CModalBody,
      CModalFooter,
      CFormInput,
      CFormTextarea,
      CRow,
      CCol,
      CBadge,
      CAlert,
      CListGroup,
      CListGroupItem,
      CFormCheck,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilNotes, cilSend, cilTrash, cilMicrophone, cilMic } from '@coreui/icons'
import { AuthUserContext } from '../../session/context';
import config from '../../../utils/EnvironmentSwitcher';
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { getAuth } from 'firebase/auth';
import { ensureFhirUserIdentifier } from '../../../utils/fhirIdentifier';
import { callAI } from '../../../services/aiChatService';

// Note component to display individual notes
const NoteCard = ({ note, onEdit, onDelete, onExportToFHIR }) => {
      return (
            <CCard
                  className="mb-3 note-card"
                  style={{
                        cursor: 'pointer',
                        backgroundColor: '#ffffff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        minHeight: '100px',
                        position: 'relative'
                  }}
            >
                  <CCardBody onClick={() => onEdit(note)}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                              <h4 style={{ color: '#000000' }}>
                                    {note.title}
                              </h4>
                        </div>

                        <p style={{ color: '#000000', whiteSpace: 'pre-wrap' }}>{note.content}</p>

                        <small style={{ color: '#666666', display: 'block', marginTop: '8px' }}>
                              Created: {new Date(note.date).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                              })}
                        </small>
                  </CCardBody>

                  <div style={{
                        position: 'absolute',
                        bottom: '10px',
                        right: '10px',
                        display: 'flex',
                        gap: '5px',
                        zIndex: 1
                  }}>
                        <CButton
                              color="danger"
                              size="sm"
                              onClick={(e) => {
                                    e.stopPropagation()
                                    onDelete(note.id)
                              }}
                        >
                              <CIcon icon={cilTrash} />
                        </CButton>
                        <CButton
                              color="info"
                              size="sm"
                              onClick={(e) => {
                                    e.stopPropagation()
                                    onExportToFHIR(note)
                              }}
                        >
                              <CIcon icon={cilSend} />
                        </CButton>
                  </div>
            </CCard>
      )
}

const Notes = () => {
      const [notes, setNotes] = useState([])
      const [showModal, setShowModal] = useState(false)
      const [activeNote, setActiveNote] = useState({ title: '', content: '' })
      const [isEditing, setIsEditing] = useState(false)
      const { authUser } = useContext(AuthUserContext)
      const fhirEndpoint = config.fhirUrl
      const [isRecording, setIsRecording] = useState(false);
      const [recognition, setRecognition] = useState(null);
      const [loading, setLoading] = useState(true)

      // Update useEffect to fetch FHIR notes on mount
      useEffect(() => {
            fetchFHIRNotes()
      }, [authUser])

      // State for AI processing
      const [aiProcessing, setAiProcessing] = useState(false)

      // AI function to extract task metadata from note content
      const extractTaskMetadata = async (text) => {
            try {
                  const content = await callAI({
                        systemMessage: 'Extract task metadata from the given text. Reply with ONLY a JSON object (no prose) with: dueTime (ISO string if found), category (string), isUrgent (boolean), priority (low/medium/high). If no task is detected, reply with {}.',
                        prompt: text,
                  });

                  // The model may wrap the JSON in extra text; pull out the first {...} block.
                  const match = content && content.match(/\{[\s\S]*\}/);
                  if (match) {
                        try {
                              return JSON.parse(match[0]);
                        } catch (e) {
                              console.warn('Failed to parse AI response as JSON');
                              return {};
                        }
                  }

                  return {};
            } catch (error) {
                  console.error('Error extracting task metadata:', error);
                  return {};
            }
      };

      // Function to create FHIR Task
      const createFHIRTask = async (noteId, metadata) => {
            try {
                  if (!authUser || !metadata.dueTime) return null;

                  const { getAuth } = await import('firebase/auth');
                  const auth = getAuth();
                  const currentUser = auth.currentUser;
                  if (!currentUser) return null;

                  const token = await currentUser.getIdToken();

                  const task = ensureFhirUserIdentifier({
                        resourceType: 'Task',
                        status: 'requested',
                        intent: 'order',
                        priority: metadata.isUrgent ? 'urgent' : 'routine',
                        description: metadata.description || 'AI-detected task',
                        for: {
                              reference: `Patient/${authUser.uid}`
                        },
                        executionPeriod: {
                              start: new Date().toISOString(),
                              end: metadata.dueTime
                        },
                        identifier: [{
                              system: 'https://fhir.mieros.cloud/fhir/notes',
                              value: noteId
                        }]
                  }, currentUser.uid);

                  if (metadata.category) {
                        task.code = {
                              coding: [{
                                    system: 'http://loinc.org',
                                    code: metadata.category,
                                    display: metadata.category
                              }]
                        };
                  }

                  const response = await fetch(`${fhirEndpoint}/fhir/Task`, {
                        method: 'POST',
                        headers: {
                              'Content-Type': 'application/fhir+json',
                              'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify(task)
                  });

                  if (response.ok) {
                        return await response.json();
                  } else {
                        console.error('Failed to create FHIR Task:', response.status);
                        return null;
                  }
            } catch (error) {
                  console.error('Error creating FHIR Task:', error);
                  return null;
            }
      };

// Fetch notes from FHIR server
const fetchFHIRNotes = async () => {
      try {
            if (!authUser) return

            // Get Firebase ID token for FHIR authorization
            const { getAuth } = await import('firebase/auth');
            const auth = getAuth();
            const currentUser = auth.currentUser;
            if (!currentUser) {
                  setLoading(false);
                  return;
            }
            const token = await currentUser.getIdToken();

            const queryUrl = `${fhirEndpoint}/fhir/DocumentReference?identifier=https://fhir.mieros.cloud/fhir/ids|${authUser.uid}`

            const response = await fetch(queryUrl, {
                  headers: {
                        'Content-Type': 'application/fhir+json',
                        'Authorization': `Bearer ${token}`
                  }
            })

            if (!response.ok) {
                  throw new Error(`HTTP error! status: ${response.status}`)
            }

            const result = await response.json()

            if (result.entry && result.entry.length > 0) {
                  const fhirNotes = result.entry.map(entry => {
                        const resource = entry.resource
                        try {
                              const base64Data = resource.content[0].attachment.data
                              const decodedData = atob(base64Data)
                              let noteData

                              try {
                                    // Try parsing as JSON first (new format)
                                    noteData = JSON.parse(decodedData)
                              } catch (e) {
                                    // If JSON parsing fails, treat as plain text (old format)
                                    noteData = {
                                          title: resource.content[0].attachment.title || 'Untitled Note',
                                          content: decodedData.trim()
                                    }
                              }

                              return {
                                    id: resource.id,
                                    title: noteData.title || 'Untitled Note',
                                    content: noteData.content || noteData,
                                    date: resource.date || resource.meta.lastUpdated || resource.content[0].attachment.creation
                              }
                        } catch (error) {
                              console.warn(`Failed to process note ${resource.id}:`, error)
                              return null
                        }
                  }).filter(note => note !== null) // Remove any failed notes

                  // Merge FHIR notes with local notes
                  const localNotes = JSON.parse(localStorage.getItem('notes') || '[]')
                  const mergedNotes = [...localNotes, ...fhirNotes]
                  // Remove duplicates and sort by date
                  const uniqueNotes = Array.from(new Map(mergedNotes.map(note => [note.id, note])).values())
                  const sortedNotes = uniqueNotes.sort((a, b) => new Date(b.date) - new Date(a.date))

                  setNotes(sortedNotes)
                  localStorage.setItem('notes', JSON.stringify(sortedNotes))
            }
      } catch (error) {
            console.error('Error fetching FHIR notes:', error)
      } finally {
            setLoading(false)
      }
}

// Update useEffect to fetch FHIR notes on mount
useEffect(() => {
      fetchFHIRNotes()
}, [authUser])

// Improved function to clean up transcripts
const cleanTranscript = (text) => {
      // Split into sentences
      const sentences = text.split(/[.!?]\s+/);

      // Process each sentence
      const cleanedSentences = sentences.map(sentence => {
            // Split into words and remove duplicates
            const words = sentence.toLowerCase().split(' ');
            const uniqueWords = words.filter((word, index, array) => {
                  // Check if this word is not a partial of the next word
                  const nextWord = array[index + 1];
                  if (nextWord && nextWord.startsWith(word)) {
                        return false;
                  }
                  // Check if this is not a duplicate of previous word
                  const prevWord = array[index - 1];
                  return word !== prevWord;
            });

            // Capitalize first word of sentence
            if (uniqueWords.length > 0) {
                  uniqueWords[0] = uniqueWords[0].charAt(0).toUpperCase() + uniqueWords[0].slice(1);
            }

            return uniqueWords.join(' ');
      });

      // Join sentences back together
      return cleanedSentences.join('. ').trim();
};

// Update the recognition onresult handler
useEffect(() => {
      if ('webkitSpeechRecognition' in window) {
            const recognition = new window.webkitSpeechRecognition();
            recognition.continuous = true;
            recognition.interimResults = true;

            recognition.onresult = (event) => {
                  const transcript = Array.from(event.results)
                        .map(result => result[0].transcript)
                        .join(' ');

                  // Clean up transcript
                  const cleaned = cleanTranscript(transcript);

                  setActiveNote(prevNote => ({
                        ...prevNote,
                        content: cleaned
                  }));
            };

            recognition.onerror = (event) => {
                  console.error('Speech recognition error:', event.error);
                  setIsRecording(false);
            };

            setRecognition(recognition);
      }
}, []);

// Toggle recording
const toggleRecording = useCallback(() => {
      if (!recognition) {
            toast.error('Speech recognition is not supported in your browser');
            return;
      }

      if (isRecording) {
            recognition.stop();
            setIsRecording(false);
      } else {
            setShowModal(true);
            recognition.start();
            setIsRecording(true);
      }
}, [recognition, isRecording]);

// Load notes from localStorage on component mount
useEffect(() => {
      const savedNotes = localStorage.getItem('notes');
      if (savedNotes) {
            const parsedNotes = JSON.parse(savedNotes);
            // Merge with existing FHIR notes
            const mergedNotes = [...parsedNotes, ...notes];
            // Remove duplicates and sort by date
            const uniqueNotes = Array.from(new Map(mergedNotes.map(note => [note.id, note])).values());
            const sortedNotes = uniqueNotes.sort((a, b) => new Date(b.date) - new Date(a.date));
            setNotes(sortedNotes);
      }
}, []);


// Update handleSave to sync with localStorage AND AI processing
const handleSave = async () => {
      if (activeNote.title.trim() === '' && activeNote.content.trim() === '') {
            setShowModal(false);
            return;
      }

      setAiProcessing(true);

      try {
            // AI Processing: Extract task metadata from note content
            const combinedText = `${activeNote.title}. ${activeNote.content}`;
            const metadata = await extractTaskMetadata(combinedText);

            console.log('AI Extracted Metadata:', metadata);

            const newNote = {
                  ...activeNote,
                  date: new Date().toISOString(),
                  id: isEditing ? activeNote.id : Date.now().toString(),
                  metadata: metadata // Store AI-extracted metadata
            };

            let updatedNotes;
            if (isEditing) {
                  updatedNotes = notes.map((note) =>
                        note.id === activeNote.id ? newNote : note
                  );
            } else {
                  updatedNotes = [newNote, ...notes];
            }

            // Update state and localStorage
            setNotes(updatedNotes);
            localStorage.setItem('notes', JSON.stringify(updatedNotes));

            // If AI detected a task with due time, create FHIR Task
            if (metadata.dueTime && !isEditing) {
                  console.log('🤖 AI detected task with due time:', metadata.dueTime);
                  const fhirTask = await createFHIRTask(newNote.id, metadata);
                  if (fhirTask) {
                        console.log('✅ FHIR Task created successfully:', fhirTask);
                        toast.success(`Task reminder created for ${new Date(metadata.dueTime).toLocaleTimeString()}`);
                  } else {
                        console.warn('⚠️ FHIR Task creation returned null - check errors above');
                        toast.warning('Note saved, but task reminder failed to create');
                  }
            } else if (!metadata.dueTime) {
                  console.log('ℹ️ No due time detected - note saved without task');
            }

            setShowModal(false);
            setActiveNote({ title: '', content: '' });
            setIsEditing(false);
      } catch (error) {
            console.error('Error saving note:', error);
            toast.error('Failed to process note with AI');
      } finally {
            setAiProcessing(false);
      }
};

const handleEdit = (note) => {
      setActiveNote(note)
      setIsEditing(true)
      setShowModal(true)
}

const exportToFHIR = async (note) => {
      try {
            if (!authUser) {
                  alert('Please log in to export notes to FHIR')
                  return
            }

            // Get Firebase ID token for FHIR authorization
            const { getAuth } = await import('firebase/auth');
            const auth = getAuth();
            const currentUser = auth.currentUser;
            if (!currentUser) {
                  alert('Not authenticated');
                  return;
            }
            const token = await currentUser.getIdToken();

            const documentReference = {
                  resourceType: 'DocumentReference',
                  status: 'current',
                  type: {
                        coding: [{
                              system: 'http://loinc.org',
                              code: '34109-9',
                              display: 'Note'
                        }]
                  },
                  content: [{
                        attachment: {
                              contentType: 'text/plain',
                              data: btoa(JSON.stringify({
                                    title: note.title || 'Untitled Note',
                                    content: note.content
                              }))
                        }
                  }],
                  date: note.date || new Date().toISOString(),
                  identifier: [{
                        system: 'https://fhir.mieros.cloud/fhir/ids',
                        value: authUser.uid
                  }]
            }

            const response = await fetch(`${fhirEndpoint}/fhir/DocumentReference`, {
                  method: 'POST',
                  headers: {
                        'Content-Type': 'application/fhir+json',
                        'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify(documentReference)
            })

            if (!response.ok) {
                  if (response.status === 401) {
                        throw new Error('Your session has expired. Please log in again.')
                  }
                  throw new Error(`HTTP error! status: ${response.status}`)
            }

            const result = await response.json()
            console.log('Successfully exported to FHIR:', result)
            alert('Note exported to FHIR successfully!')

            // Refresh notes after export
            fetchFHIRNotes()
      } catch (error) {
            console.error('Error exporting to FHIR:', error)
            alert(error.message || 'Failed to export note to FHIR')
      }
}

// Update deleteNote to sync with localStorage
const deleteNote = async (noteId) => {
      try {
            if (!authUser) {
                  alert('Please log in to delete notes');
                  return;
            }

            // Confirm deletion
            if (!window.confirm('Are you sure you want to delete this note?')) {
                  return;
            }

            // Get Firebase ID token for FHIR authorization
            const { getAuth } = await import('firebase/auth');
            const auth = getAuth();
            const currentUser = auth.currentUser;
            if (!currentUser) {
                  alert('Not authenticated');
                  return;
            }
            const token = await currentUser.getIdToken();

            // Delete from FHIR server
            const response = await fetch(`${fhirEndpoint}/fhir/DocumentReference/${noteId}`, {
                  method: 'DELETE',
                  headers: {
                        'Content-Type': 'application/fhir+json',
                        'Authorization': `Bearer ${token}`
                  }
            });

            if (!response.ok) {
                  if (response.status === 401) {
                        throw new Error('Your session has expired. Please log in again.');
                  }
                  throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Remove from local state and localStorage
            const updatedNotes = notes.filter(note => note.id !== noteId);
            setNotes(updatedNotes);
            localStorage.setItem('notes', JSON.stringify(updatedNotes));

            console.log('Note deleted successfully');
      } catch (error) {
            console.error('Error deleting note:', error);
            alert(error.message || 'Failed to delete note');
      }
};

return (
      <>
            {/* Main container */}
            <div style={{ padding: '20px' }}>
                  {loading ? (
                        <div>Loading notes...</div>
                  ) : (
                        <>

                              {/* Create Note Button */}
                              <CButton
                                    color="primary"
                                    className="mb-4"
                                    onClick={() => {
                                          setActiveNote({ title: '', content: '' })
                                          setIsEditing(false)
                                          setShowModal(true)
                                    }}
                              >
                                    <CIcon icon={cilNotes} className="me-2" />
                                    Take a note...
                              </CButton>

                              {/* Notes Grid */}
                              <CRow>
                                    {notes.map((note) => (
                                          <CCol key={note.id} xs={12} sm={6} md={4} lg={3}>
                                                <NoteCard
                                                      note={note}
                                                      onEdit={handleEdit}
                                                      onDelete={deleteNote}
                                                      onExportToFHIR={exportToFHIR}
                                                />
                                          </CCol>
                                    ))}
                              </CRow>
                        </>
                  )}
            </div>

            {/* Note Modal */}
            <CModal
                  visible={showModal}
                  onClose={() => {
                        if (isRecording) {
                              recognition?.stop();
                              setIsRecording(false);
                        }
                        setShowModal(false);
                        setActiveNote({ title: '', content: '' });
                        setIsEditing(false);
                  }}
                  backdrop="static"
                  size="lg"
                  dark
            >
                  <CModalHeader>
                        <CModalTitle>
                              <CFormInput
                                    placeholder="Title"
                                    value={activeNote.title}
                                    onChange={(e) => setActiveNote({ ...activeNote, title: e.target.value })}
                                    style={{
                                          border: 'none',
                                          backgroundColor: 'transparent',
                                          color: '#000000',
                                          fontSize: '1.25rem',
                                          padding: '0'
                                    }}
                              />
                        </CModalTitle>
                  </CModalHeader>
                  <CModalBody>
                        <CFormTextarea
                              placeholder="Take a note..."
                              value={activeNote.content}
                              onChange={(e) => setActiveNote({ ...activeNote, content: e.target.value })}
                              rows={10}
                              style={{
                                    border: 'none',
                                    backgroundColor: 'transparent',
                                    color: '#000000'
                              }}
                        />
                  </CModalBody>
                  <CModalFooter>
                        <div className="d-flex justify-content-between align-items-center w-100">
                              <div className="d-flex gap-2">
                                    <CButton
                                          color={isRecording ? "danger" : "primary"}
                                          onClick={toggleRecording}
                                    >
                                          <CIcon icon={isRecording ? cilMic : cilMic} className="me-2" />
                                          {isRecording ? 'Stop Recording' : 'Start Recording'}
                                    </CButton>
                                    <CButton color="success" onClick={() => exportToFHIR(activeNote)}>
                                          <CIcon icon={cilSend} className="me-2" />
                                          Export to FHIR
                                    </CButton>
                              </div>
                              <div className="d-flex gap-2">
                                    {aiProcessing && (
                                          <CBadge color="info" className="me-2 d-flex align-items-center">
                                                Processing with AI...
                                          </CBadge>
                                    )}
                                    <CButton color="secondary" onClick={() => {
                                          if (isRecording) {
                                                recognition?.stop();
                                                setIsRecording(false);
                                          }
                                          setShowModal(false);
                                          setActiveNote({ title: '', content: '' });
                                          setIsEditing(false);
                                    }}>
                                          Close
                                    </CButton>
                                    <CButton
                                          color="primary"
                                          onClick={handleSave}
                                          disabled={aiProcessing}
                                    >
                                          Save {aiProcessing ? '...' : ''}
                                    </CButton>
                              </div>
                        </div>
                  </CModalFooter>
            </CModal>
      </>
)
}

export default Notes
